import { type Geometry } from 'geojson';
// ==================== ENUMS ====================
// These are safe to use in both client and server

export enum GeoObjectType {
  country = 'country',
  region = 'region',
  place = 'place',
  port = 'port',
  inland_port = 'inland_port',
  airport = 'airport',
  rail_terminal = 'rail_terminal',
  trade_regions = 'trade_regions',
}

export enum OsmType {
  node = 'node',
  way = 'way',
  relation = 'relation',
}

export enum AdminLevel {
  adm0 = 'adm0', // Country / Territory
  adm1 = 'adm1', // Region / State / Province / Oblast / Republic / Krai / Autonomous Region / County
  adm2 = 'adm2', // District / Urban District / City / Local Council / Commune / Village
}

export enum BorderSource {
  osm_nominatim = 'osm_nominatim', // Fetched from OSM Nominatim API
  osm_polygons = 'osm_polygons', // Fetched from OSM Polygons API
  manual = 'manual', // Manually edited/drawn by user
}

// ==================== RESPONSE DTOs (Interfaces) ====================
// These are safe to use in both client and server

export interface GeoObjectTranslationResponseDto {
  id: string;
  geoObjectId: string;
  languageCode: string;
  name: string;
  nameNormalized: string | null;
  description: string | null;
  source: string | null;
  isPrimary: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ==================== ALTERNATIVE NAME DTOs ====================

export interface GeoObjectAlternativeNameResponseDto {
  id: string;
  geoObjectId: string;
  name: string;
  source: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// ==================== BORDER DTOs ====================

export interface GeoObjectBorderResponseDto {
  id: string;
  geoObjectId: string;
  geometry: Geometry;
  source: BorderSource;
  isLandClipped: boolean;
  simplificationLevel: number | null;
  vertexCount: number | null;
  fileSizeBytes: number | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface GeoObjectBorderWithFallbackResponseDto {
  border: GeoObjectBorderResponseDto | null;
  geometry: Geometry | null;
  source: 'database' | 'osm_live';
  isLandClipped: boolean;
}

export interface GeoObjectBorderMapItemDto {
  geoObjectId: string;
  geometry: Geometry;
  parentGeoObjectId?: string;
  parentGeoObjectIds?: string[];
}

export interface GeoObjectBordersMapResponseDto {
  borders: GeoObjectBorderMapItemDto[];
  count: number;
}

export interface CountryBorderMapItemDto {
  geoObjectId: string;
  isoCountryCode: string | null;
  geometry: Geometry;
}

export interface CountryBordersMapResponseDto {
  borders: CountryBorderMapItemDto[];
  count: number;
}

export interface UpdateGeoObjectBorderData {
  geometry: Geometry;
  source: BorderSource;
  isLandClipped?: boolean;
  simplificationLevel?: number;
}

export interface FetchOsmBorderParams {
  clipToLand?: boolean;
  saveToDb?: boolean;
}

export interface GeoObjectResponseDto {
  id: string;
  name: string;
  alternativeNames: GeoObjectAlternativeNameResponseDto[];
  slug: string;
  type: GeoObjectType;
  adminLevel: AdminLevel | null;
  countryId: string | null;
  regionId: string | null;
  isoCountryCode: string | null;
  isoCountryCodeAlpha3: string | null;
  isoSubdivisionCode: string | null;
  unlocode: string | null;
  iataCode: string | null;
  icaoCode: string | null;
  // Mapbox ID (primary geocoding provider)
  mapboxId: string | null;
  // Legacy OSM fields
  osmId: string | null; // Serialized as string since BigInt cannot be JSON-serialized
  osmType: OsmType | null;
  osmPlaceRank: number | null;
  osmAdminLevel: number | null; // Raw OSM admin_level (1-15)
  // Wikidata reference
  wikidataId: string | null;
  latitude: number | null;
  longitude: number | null;
  bboxMinLat: number | null;
  bboxMaxLat: number | null;
  bboxMinLng: number | null;
  bboxMaxLng: number | null;
  // Image URL (CDN URL from uploaded image)
  imageUrl: string | null;
  // Raw geocoding response from API (OSM/Mapbox)
  geocodingResponse: Record<string, unknown> | null;
  // Container port flag - true for ports that handle container shipping
  isContainerPort: boolean;
  // Supported flag - true for locations that are supported/served
  isSupported: boolean;
  isActive: boolean;
  isVerified: boolean;
  createdAt: Date;
  updatedAt: Date;
  translations?: GeoObjectTranslationResponseDto[];
}

export interface GeoObjectRelationshipDto {
  id: string;
  parentGeoObjectId: string;
  childGeoObjectId: string;
  isPrimary: boolean;
  geoObject: GeoObjectResponseDto;
}

export interface GeoObjectDetailResponseDto extends GeoObjectResponseDto {
  translations: GeoObjectTranslationResponseDto[];
  country?: GeoObjectResponseDto | null;
  region?: GeoObjectResponseDto | null;
  parentGeoObjects?: GeoObjectRelationshipDto[];
  childGeoObjects?: GeoObjectRelationshipDto[];
  /** @deprecated Use childGeoObjects instead */
  children?: GeoObjectResponseDto[];
}

export interface GeoObjectsResponseDto {
  data: GeoObjectResponseDto[];
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface GeoObjectTreeNodeDto {
  id: string;
  name: string;
  type: GeoObjectType;
  isoCountryCode: string | null;
  unlocode: string | null;
  children?: GeoObjectTreeNodeDto[];
}

// ==================== PUBLIC DTOs ====================

export interface PublicGeoObjectDto {
  id: string;
  name: string;
  type: GeoObjectType;
  isoCountryCode: string | null;
  unlocode: string | null;
  coordinates: [number, number] | null;
}

// ==================== REQUEST INTERFACES ====================
// Plain interfaces for client-side use (no decorators)

export interface GetGeoObjectsParams {
  page?: number;
  limit?: number;
  type?: GeoObjectType;
  countryId?: string;
  search?: string;
  isActive?: boolean;
  isVerified?: boolean;
  isSupported?: boolean;
}

export interface GetGeoObjectTreeParams {
  rootType?: GeoObjectType;
  depth?: number;
}

export interface CreateGeoObjectData {
  name: string;
  alternativeNames?: string[];
  slug?: string;
  type: GeoObjectType;
  adminLevel?: AdminLevel;
  parentGeoObjectId?: string;
  isoCountryCode?: string;
  isoCountryCodeAlpha3?: string;
  isoSubdivisionCode?: string;
  unlocode?: string;
  iataCode?: string;
  icaoCode?: string;
  mapboxId?: string;
  osmId?: string;
  osmType?: OsmType;
  osmPlaceRank?: number;
  osmAdminLevel?: number;
  wikidataId?: string;
  latitude?: number;
  longitude?: number;
  bboxMinLat?: number;
  bboxMaxLat?: number;
  bboxMinLng?: number;
  bboxMaxLng?: number;
  geocodingResponse?: Record<string, unknown>;
  isSupported?: boolean;
  isActive?: boolean;
}

export interface UpdateGeoObjectData {
  name?: string;
  alternativeNames?: string[];
  slug?: string;
  type?: GeoObjectType;
  adminLevel?: AdminLevel;
  isoCountryCode?: string;
  isoCountryCodeAlpha3?: string;
  isoSubdivisionCode?: string;
  unlocode?: string | null;
  iataCode?: string;
  icaoCode?: string;
  mapboxId?: string;
  osmId?: string;
  osmType?: OsmType;
  osmPlaceRank?: number;
  osmAdminLevel?: number;
  wikidataId?: string;
  latitude?: number;
  longitude?: number;
  bboxMinLat?: number;
  bboxMaxLat?: number;
  bboxMinLng?: number;
  bboxMaxLng?: number;
  geocodingResponse?: Record<string, unknown>;
  isContainerPort?: boolean;
  isSupported?: boolean;
  isActive?: boolean;
}

// ==================== EXPORT DTOs ====================

export interface CountryExportDto {
  countryName: string;
  regionCount: number;
}

export interface RegionExportDto {
  countryName: string;
  regionName: string;
}

export type GeoObjectExportType = 'country' | 'region';

export interface GeoObjectExportResponseDto {
  type: GeoObjectExportType;
  data: CountryExportDto[] | RegionExportDto[];
}

/** Generic export item containing all useful geo object fields */
export interface GeoObjectExportItemDto {
  name: string;
  nameEn: string;
  type: GeoObjectType;
  slug: string;
  isoCountryCode: string | null;
  isoCountryCodeAlpha3: string | null;
  isoSubdivisionCode: string | null;
  unlocode: string | null;
  iataCode: string | null;
  icaoCode: string | null;
  mapboxId: string | null;
  osmId: string | null;
  wikidataId: string | null;
  latitude: number | null;
  longitude: number | null;
  isActive: boolean;
  isVerified: boolean;
  isSupported: boolean;
  isContainerPort: boolean;
  countryName: string | null;
  regionName: string | null;
  alternativeNames: string;
  createdAt: string;
  updatedAt: string;
}

export interface GeoObjectGenericExportResponseDto {
  data: GeoObjectExportItemDto[];
  count: number;
}

export interface GeoObjectExportParams {
  type?: GeoObjectType;
  search?: string;
  isActive?: boolean;
  isVerified?: boolean;
  isSupported?: boolean;
}

// ==================== OSM LOOKUP DTOs ====================

/**
 * Response from OSM lookup endpoint
 * Contains prefilled geo object data from Nominatim
 */
export interface OsmLookupResponseDto {
  /** Suggested name from OSM */
  name: string;
  /** Detected GeoObjectType based on OSM admin_level */
  type: GeoObjectType;
  /** Mapped AdminLevel (adm0, adm1, adm2) */
  adminLevel: AdminLevel | null;
  /** OSM ID as string */
  osmId: string;
  /** OSM type (node, way, relation) */
  osmType: OsmType;
  /** Raw OSM admin_level value (2-10) */
  osmAdminLevel: number | null;
  /** OSM place_rank value */
  osmPlaceRank: number | null;
  /** Wikidata ID if available */
  wikidataId: string | null;
  /** ISO 3166-1 alpha-2 country code */
  isoCountryCode: string | null;
  /** ISO 3166-1 alpha-3 country code */
  isoCountryCodeAlpha3: string | null;
  /** Center latitude */
  latitude: number | null;
  /** Center longitude */
  longitude: number | null;
  /** Bounding box */
  bboxMinLat: number | null;
  bboxMaxLat: number | null;
  bboxMinLng: number | null;
  bboxMaxLng: number | null;
  /** Full display name from Nominatim */
  displayName: string;
  /** Raw geocoding response for storage */
  geocodingResponse: Record<string, unknown>;
  /** Suggested parent geo object (found by country code or OSM hierarchy) */
  suggestedParent?: {
    id: string;
    name: string;
    type: GeoObjectType;
  } | null;
  /** Suggested translations extracted from OSM namedetails */
  suggestedTranslations?: {
    languageCode: string;
    name: string;
  }[];
}
